<script setup lang="ts">
import Document from '@tiptap/extension-document'
import Mention from '@tiptap/extension-mention'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Link from '@tiptap/extension-link'
import { Editor, EditorContent, mergeAttributes, VueNodeViewRenderer } from '@tiptap/vue-3'
import { Placeholder } from '@tiptap/extensions'
import { onBeforeUnmount, onMounted, provide, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/config/store/userStore'
import { createMentionSuggestion, type MentionablePerson } from './suggestion'
import MentionChipNodeView from './MentionChipNodeView.vue'

const editor = ref<Editor | null>(null)
const { t } = useI18n()
const userStore = useUserStore()

const ExtendedMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      firstname: {
        default: null,
        parseHTML: element => element.getAttribute('data-firstname'),
        renderHTML: (attributes) => {
          if (!attributes.firstname) return {}
          return { 'data-firstname': attributes.firstname }
        },
      },
      lastname: {
        default: null,
        parseHTML: element => element.getAttribute('data-lastname'),
        renderHTML: (attributes) => {
          if (!attributes.lastname) return {}
          return { 'data-lastname': attributes.lastname }
        },
      },
      // Kept in the editor state for the NodeView lookup fallback — never persisted in HTML
      picture: {
        default: null,
        parseHTML: () => null,
        renderHTML: () => ({}),
      },
    }
  },
  addNodeView() {
    return VueNodeViewRenderer(MentionChipNodeView)
  },
})

const props = withDefaults(defineProps<{
  modelValue: string
  mentions: Array<{
    _id: string
    label: string
    firstName: string
    lastName: string
    picture: string
  }>
  placeholder?: string
  /** Skip card-base chrome when the parent already provides a bordered shell */
  bare?: boolean
  /**
   * When set, @ suggestions are limited to this list (e.g. conversation members).
   * When omitted, falls back to global resource search (comments, etc.).
   */
  mentionables?: MentionablePerson[] | null
}>(), {
  placeholder: 'Type @ to mention',
  bare: false,
  mentionables: undefined,
})

const emits = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'update:mentions', value: Array<{ _id: string; firstname: string; lastname: string; picture: string }>): void
  (e: 'submit'): void
}>()

const mentionablesRef = toRef(props, 'mentionables')
provide('mentionables', mentionablesRef)

const myResourceId = () => String(userStore.user?._id || '')

onMounted(() => {
  editor.value = new Editor({
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault()
          emits('submit')
          return true
        }
        return false
      },
    },
    extensions: [
      Document,
      Paragraph,
      Text,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'underline break-all text-primary-500',
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      ExtendedMention.configure({
        // Persisted HTML: identity only — photos are hydrated on read from API `mentions`
        renderHTML({ options, node }) {
          const id = String(node.attrs.id || '')
          const isMe = !!id && id === myResourceId()
          const firstname = node.attrs.firstname || ''
          const lastname = node.attrs.lastname || ''
          const label = isMe
            ? t('modules.chat.mentionYou')
            : `${firstname} ${lastname}`.trim() || String(node.attrs.label || '')

          return [
            'span',
            mergeAttributes(
              { 'data-type': 'mention', class: 'mention' },
              options.HTMLAttributes,
              {
                'data-id': id,
                'data-firstname': firstname,
                'data-lastname': lastname,
                'data-label': label,
              },
            ),
            `@${label}`,
          ]
        },
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: createMentionSuggestion(() => mentionablesRef.value),
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
    ],
    content: props.modelValue,
  })

  editor.value.on('update', () => {
    if (!editor.value) return
    const html = editor.value.getHTML()
    emits('update:modelValue', html)

    const nextMentions: Array<{ _id: string; firstname: string; lastname: string; picture: string }> = []
    editor.value.state.doc.descendants((node) => {
      if (node.type.name === 'mention') {
        nextMentions.push({
          _id: node.attrs.id,
          firstname: node.attrs.firstname,
          lastname: node.attrs.lastname,
          picture: node.attrs.picture,
        })
      }
    })
    emits('update:mentions', nextMentions)
  })
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (editor.value && editor.value.getHTML() !== newValue) {
      editor.value.commands.setContent(newValue)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<template>
  <div
    v-if="editor"
    class="mention-area min-w-0"
    :class="bare ? '' : 'card-base'"
  >
    <EditorContent :editor="editor" />
  </div>
</template>

<style>
.mention-area .tiptap {
  outline: none;
}

.mention-area .tiptap p {
  margin: 0;
}

.mention-area .tiptap p.is-editor-empty:first-child::before {
  color: rgb(156 163 175);
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.dark .mention-area .tiptap p.is-editor-empty:first-child::before {
  color: rgb(156 163 175 / 0.7);
}

.mention-area__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0.1rem;
  padding: 0.05rem 0.4rem 0.05rem 0.15rem;
  border-radius: 999px;
  background: rgb(220 38 127 / 0.14);
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  vertical-align: middle;
}

.mention-area__chip--me {
  background: rgb(245 158 11 / 0.28);
  box-shadow: inset 0 0 0 1px rgb(245 158 11 / 0.45);
  color: rgb(146 64 14);
}

.dark .mention-area__chip--me {
  color: rgb(253 230 138);
}

.mention-area__avatar-wrap {
  flex-shrink: 0;
}

.mention-area__label {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
